import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  LayoutGrid,
  List as ListIcon,
  Search,
  X,
  Filter,
  Package,
  Building2,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Ticket as TicketIcon,
} from "lucide-react";
import moment from "moment";

import { getAllIncidents } from "../../../api/packages";
import { getActiveClientsApi } from "../../../api/company";
import { showToast } from "../../../utils/showToast";
import useAuth from "../../../hooks/useAuth";
import useUrlState, { useUrlStateBatch } from "../../../hooks/useUrlState";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useIsMobile } from "../../../hooks/useIsMobile";

import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../../components/ui/Avatar";
import { Select } from "../../../components/ui/Select";
import { EmptyState } from "../../../components/ui/EmptyState";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Table, THead, TBody, TR, TH, TD } from "../../../components/ui/Table";
import SearchDropdown from "../../../components/searchDropdown";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import { getInitials } from "../../../utils/getInitials";
import { formatNumberCL } from "../../../utils/formatters";
import { cn } from "../../../utils/cn";

import {
  INCIDENT_SEVERITY,
  INCIDENT_RESPONSIBLE,
  INCIDENT_TYPES_BY_CATEGORY,
  INCIDENT_CATEGORIES,
  DEPARTMENT_LABELS,
} from "../../../constants/incidents";

import TicketBoard from "./TicketBoard";
import TicketDetailDrawer from "./TicketDetailDrawer";
import {
  SEVERITY_TONE,
  COLUMN_TONE,
  TICKET_COLUMNS,
  columnFor,
  typeLabel,
  clientName,
  followingNumber,
  assignee,
  primaryDepartment,
} from "./ticketModel";

import "../../../components/incidentDetail/IncidentDetail.scss";

const PAGE_LIMIT = 200; // board necesita el set completo para agrupar

// Rango de urgencia por criticidad (alias EN incluidos). Mayor = más urgente.
const SEVERITY_RANK = {
  critica: 4,
  critical: 4,
  alta: 3,
  high: 3,
  media: 2,
  medium: 2,
  baja: 1,
  low: 1,
};

// Columnas "cerradas" para el orden por urgencia (abiertos van primero).
const CLOSED_COLUMNS = new Set(["resuelto", "cerrado"]);

// Deriva el rango concreto (YYYY-MM-DD) desde un preset de la URL, para hidratar
// el estado de fecha en el primer render sin tocar la capa de datos. "" o
// "custom" (no reconstruible desde la URL) ⇒ sin rango.
function presetToRange(preset) {
  if (!preset || preset === "custom") return { start: "", end: "" };
  const [s, e] = rangeFromPreset(preset);
  return { start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD") };
}

export default function IncidentsView() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const logisticCompanyId = user?.logisticCompany?._id || null;

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);

  // Vista board | list (persistida en la URL; push para que el "back" del
  // navegador vuelva al toggle anterior). En mobile el default es 'list': el
  // board kanban apila columnas y su drag&drop no funciona en touch.
  const [view, setView] = useUrlState("view", isMobile ? "list" : "board", { push: true });

  // Búsqueda (client-side sobre el set cargado), persistida en ?q=.
  const [search, setSearch] = useUrlState("q", "");
  const debouncedSearch = useDebouncedValue(search, 250);

  // Filtros (server-side via getAllIncidents), persistidos en la URL. Los
  // defaults coinciden con los actuales: sin param ⇒ comportamiento idéntico.
  const [responsible, setResponsible] = useUrlState("responsible", "");
  const [department, setDepartment] = useUrlState("department", "");
  const [severity, setSeverity] = useUrlState("severity", "");
  const [incidentType, setIncidentType] = useUrlState("type", "");
  const [clientId, setClientId] = useUrlState("client", "");
  const [datePreset] = useUrlState("preset", "");
  // Rango custom (no derivable de un preset) persistido en ?from/?to para que el
  // reload y el back/forward reconstruyan el rango elegido a mano.
  const [customFrom] = useUrlState("from", "");
  const [customTo] = useUrlState("to", "");
  // Setter atómico de los tres params de fecha en UNA sola navegación: con
  // setters individuales de useUrlState (react-router lee searchParams del
  // closure del render) las navegaciones se pisarían entre sí.
  const setDateUrl = useUrlStateBatch({
    preset: { type: "string", defaultValue: "" },
    from: { type: "string", defaultValue: "" },
    to: { type: "string", defaultValue: "" },
  });
  // El rango concreto (YYYY-MM-DD) se deriva de la URL: del preset, o de
  // ?from/?to cuando el preset es 'custom'. Sin preset ⇒ "" ⇒ comportamiento
  // idéntico al previo (sin filtro de fecha).
  const [startDate, setStartDate] = useState(() =>
    datePreset === "custom" ? customFrom : presetToRange(datePreset).start,
  );
  const [endDate, setEndDate] = useState(() =>
    datePreset === "custom" ? customTo : presetToRange(datePreset).end,
  );
  // Mantiene startDate/endDate en sync con la URL (reactiva). El estado anterior
  // sembraba el rango UNA sola vez, así que con back/forward (la vista usa
  // push:true) el preset cambiaba y el rango quedaba stale → el filtro de fecha
  // se ocultaba pero buildParams seguía enviando el rango viejo. Ahora se re-deriva:
  // del preset, o de ?from/?to para 'custom'. En el montaje converge al valor
  // sembrado (sin fetch extra); en back/forward sí actualiza el rango y la consulta
  // se rehace (esta vista no tiene botón Consultar).
  useEffect(() => {
    if (datePreset === "custom") {
      setStartDate(customFrom);
      setEndDate(customTo);
      return;
    }
    const { start, end } = presetToRange(datePreset);
    setStartDate(start);
    setEndDate(end);
  }, [datePreset, customFrom, customTo]);

  // Quick-filter de estado desde las cards KPI (client-side, sobre `filtered`).
  const [statFilter, setStatFilter] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Clientes
  const [clientList, setClientList] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(false);

  // Detalle
  const [selectedId, setSelectedId] = useState(null);

  // Overrides de columna (drag/select optimista, front-only)
  const [localColumns, setLocalColumns] = useState({});

  useEffect(() => {
    if (!logisticCompanyId) return;
    setIsLoadingClient(true);
    getActiveClientsApi(logisticCompanyId)
      .then((clients) => {
        if (Array.isArray(clients)) {
          setClientList(
            clients.map((c) => ({ value: c?._id, label: c?.name || t("Sin nombre") })),
          );
        }
      })
      .catch(() => {})
      .finally(() => setIsLoadingClient(false));
  }, [logisticCompanyId, t]);

  const buildParams = useCallback(
    (overrides = {}) => ({
      page: 1,
      limit: PAGE_LIMIT,
      orderField: "createdAt",
      sortType: "desc",
      responsible: responsible || undefined,
      department: department || undefined,
      severity: severity || undefined,
      incidentType: incidentType || undefined,
      clientId: clientId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      ...overrides,
    }),
    [responsible, department, severity, incidentType, clientId, startDate, endDate],
  );

  const load = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      setError(false);
      try {
        const res = await getAllIncidents(buildParams(overrides));
        if (res.success && res.data) {
          setIncidents(res.data.docs || []);
          setTotalDocs(res.data.totalDocs || (res.data.docs || []).length);
        } else {
          setError(true);
          showToast(res.error?.message || t("Error al cargar los tickets"), false);
        }
      } catch {
        setError(true);
        showToast(t("Error al cargar los tickets"), false);
      } finally {
        setLoading(false);
      }
    },
    [buildParams, t],
  );

  // Carga inicial + recarga al cambiar filtros server-side
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsible, department, severity, incidentType, clientId, startDate, endDate]);

  // Filtrado por texto (client-side)
  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return incidents;
    return incidents.filter((inc) => {
      const haystack = [
        followingNumber(inc),
        typeLabel(inc),
        inc.description,
        clientName(inc),
        assignee(inc)?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [incidents, debouncedSearch]);

  // KPIs derivados del set filtrado
  const kpis = useMemo(() => {
    const counts = TICKET_COLUMNS.reduce((acc, c) => {
      acc[c.key] = 0;
      return acc;
    }, {});
    let criticos = 0;
    filtered.forEach((inc) => {
      const col = columnFor(inc, localColumns[inc._id]);
      counts[col] = (counts[col] || 0) + 1;
      if ((inc.severity === "critica" || inc.severity === "critical") && col !== "cerrado") {
        criticos += 1;
      }
    });
    const abiertos = counts.nuevo + counts.en_proceso + counts.esperando;
    return { total: filtered.length, abiertos, resueltos: counts.resuelto + counts.cerrado, criticos };
  }, [filtered, localColumns]);

  // Set visible: aplica el quick-filter de las cards KPI (client-side) y ordena
  // por urgencia (criticidad desc → abiertos antes que cerrados → creación desc).
  // Todo derivado de campos ya presentes; no toca buildParams/fetch.
  const visible = useMemo(() => {
    let list = filtered;
    if (statFilter === "abiertos") {
      list = list.filter((inc) => {
        const col = columnFor(inc, localColumns[inc._id]);
        return col === "nuevo" || col === "en_proceso" || col === "esperando";
      });
    } else if (statFilter === "resueltos") {
      list = list.filter((inc) => {
        const col = columnFor(inc, localColumns[inc._id]);
        return col === "resuelto" || col === "cerrado";
      });
    } else if (statFilter === "criticos") {
      list = list.filter((inc) => {
        const col = columnFor(inc, localColumns[inc._id]);
        const crit = inc.severity === "critica" || inc.severity === "critical";
        return crit && col !== "cerrado";
      });
    }
    return [...list].sort((a, b) => {
      const sevA = SEVERITY_RANK[a.severity] || 0;
      const sevB = SEVERITY_RANK[b.severity] || 0;
      if (sevB !== sevA) return sevB - sevA;
      const closedA = CLOSED_COLUMNS.has(columnFor(a, localColumns[a._id])) ? 1 : 0;
      const closedB = CLOSED_COLUMNS.has(columnFor(b, localColumns[b._id])) ? 1 : 0;
      if (closedA !== closedB) return closedA - closedB;
      const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tB - tA;
    });
  }, [filtered, statFilter, localColumns]);

  const activeFilters = [
    responsible,
    department,
    severity,
    incidentType,
    clientId,
    startDate,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setResponsible("");
    setDepartment("");
    setSeverity("");
    setIncidentType("");
    setClientId("");
    // startDate/endDate los limpia el effect cuando los params de fecha se van.
    setDateUrl({ preset: "", from: "", to: "" });
    setSearch("");
    setStatFilter("");
  };

  const handleMoveColumn = (incident, columnKey) => {
    setLocalColumns((prev) => ({ ...prev, [incident._id]: columnKey }));
    showToast(
      t("Ticket movido a “{{col}}” (solo en pantalla — falta endpoint).", {
        col: columnLabel(t, columnKey),
      }),
      "warning",
    );
  };

  // Cards KPI = quick-filter de estado (client-side). value "" = Total/limpia.
  // Re-click de la card activa limpia el filtro (contrato de resume).
  const statCards = [
    { key: "", label: t("Total"), value: kpis.total, tone: "brand", Icon: TicketIcon },
    { key: "abiertos", label: t("Abiertos"), value: kpis.abiertos, tone: "warning", Icon: AlertTriangle },
    { key: "resueltos", label: t("Resueltos"), value: kpis.resueltos, tone: "success", Icon: MessageSquare },
    {
      key: "criticos",
      label: t("Críticos"),
      value: kpis.criticos,
      tone: kpis.criticos > 0 ? "error" : "neutral",
      Icon: AlertTriangle,
    },
  ];

  const handleStatClick = (key) => {
    // "Total" (key "") o re-click de la card activa → limpiar el filtro.
    setStatFilter((prev) => (prev === key ? "" : key));
  };

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-5 anim-fade-in-up">
      <PageHeader
        icon={<TicketIcon className="w-6 h-6 text-fg-muted" />}
        title={t("Incidencias")}
        subtitle={t("Ticketera de incidencias: asigna, tipifica y resuelve por área.")}
        actions={
          <div className="flex items-center gap-1 rounded-lg border border-border bg-bg p-1">
            <ViewToggle
              active={view === "board"}
              onClick={() => setView("board")}
              icon={<LayoutGrid className="w-4 h-4" />}
              label={t("Board")}
            />
            <ViewToggle
              active={view === "list"}
              onClick={() => setView("list")}
              icon={<ListIcon className="w-4 h-4" />}
              label={t("Lista")}
            />
          </div>
        }
      />

      {/* ── KPIs como quick-filter de estado ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map(({ key, label, value, tone, Icon }) => {
          const isActive = statFilter === key;
          return (
            <button
              key={key || "total"}
              type="button"
              aria-pressed={isActive}
              onClick={() => handleStatClick(key)}
              className={cn(
                "rounded-lg border bg-bg-muted p-4 shadow-card flex items-center gap-3 text-left transition-all",
                "hover:border-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                isActive ? "border-brand-500 ring-1 ring-brand-500" : "border-border",
              )}
            >
              <span
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                  tone === "brand" && "bg-brand-50 text-brand-600",
                  tone === "warning" && "bg-status-warning-soft text-status-warning",
                  tone === "success" && "bg-status-success-soft text-status-success",
                  tone === "error" && "bg-status-error-soft text-status-error",
                  tone === "neutral" && "bg-status-neutral-soft text-fg-muted",
                )}
              >
                <Icon className="w-5 h-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <div className="text-2xl font-semibold text-fg tabular-nums leading-none">
                  {formatNumberCL(value)}
                </div>
                <div className="text-xs text-fg-muted mt-1">{label}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Búsqueda + fecha + toggle filtros ── */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isMobile ? t("Buscar tickets…") : t("Buscar por pedido, tipo, empresa o responsable…")}
            className="h-10 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <DateRangeFilter
          value={datePreset || ""}
          customRange={
            datePreset === "custom" ? { start: customFrom, end: customTo } : null
          }
          onChange={(s, e, preset) => {
            // El rango concreto lo deriva el effect desde la URL. Para 'custom'
            // persistimos ?from/?to (reconstruibles en reload/back); para un
            // preset los limpiamos.
            if (preset === "custom") {
              setDateUrl({ preset: "custom", from: s, to: e });
            } else {
              setDateUrl({ preset, from: "", to: "" });
            }
          }}
          onClear={() => setDateUrl({ preset: "", from: "", to: "" })}
        />
        <Button
          variant={filtersOpen || activeFilters ? "outline" : "ghost"}
          iconLeft={<Filter className="w-4 h-4" />}
          iconRight={filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          {t("Filtros")}
          {activeFilters > 0 && (
            <Badge tone="brand" className="ml-1">
              {activeFilters}
            </Badge>
          )}
        </Button>
      </div>

      {/* ── Panel de filtros ── */}
      {filtersOpen && (
        <div className="rounded-lg border border-border bg-bg-muted p-4 space-y-4 anim-fade-in-up">
          <div className="space-y-1.5">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg">
              <Building2 className="w-4 h-4 text-fg-muted" /> {t("Empresa")}
            </span>
            <SearchDropdown
              onChange={(value) => setClientId(value || "")}
              options={clientList}
              placeholder={isLoadingClient ? t("Cargando empresas...") : t("Todas las empresas")}
              selected={clientList.find((c) => c.value === clientId)?.label}
              disabled={isLoadingClient}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select
              label={t("Área")}
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder={t("Todas")}
              options={Object.entries(DEPARTMENT_LABELS).map(([k, v]) => ({ value: k, label: t(v) }))}
            />
            <Select
              label={t("Criticidad")}
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              placeholder={t("Todas")}
              options={Object.entries(INCIDENT_SEVERITY).map(([k, v]) => ({ value: k, label: t(v) }))}
            />
            <Select
              label={t("Responsable")}
              value={responsible}
              onChange={(e) => setResponsible(e.target.value)}
              placeholder={t("Todos")}
              options={Object.entries(INCIDENT_RESPONSIBLE).map(([k, v]) => ({ value: k, label: t(v) }))}
            />
            <Select
              label={t("Tipo")}
              value={incidentType}
              onChange={(e) => setIncidentType(e.target.value)}
            >
              <option value="">{t("Todos")}</option>
              {Object.entries(INCIDENT_TYPES_BY_CATEGORY).map(([catKey, types]) => (
                <optgroup key={catKey} label={t(INCIDENT_CATEGORIES[catKey] || catKey)}>
                  {Object.entries(types).map(([tk, tl]) => (
                    <option key={tk} value={tk}>
                      {t(tl)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" iconLeft={<X className="w-4 h-4" />} onClick={clearFilters}>
              {t("Limpiar filtros")}
            </Button>
          </div>
        </div>
      )}

      {/* ── Contador ── */}
      {!loading && !error && (
        <p className="text-xs text-fg-muted">
          {t("Mostrando")} {formatNumberCL(visible.length)} {t("de")} {formatNumberCL(totalDocs)}{" "}
          {t("tickets")}
        </p>
      )}

      {/* ── Contenido ── */}
      {error ? (
        <div className="rounded-lg border border-border bg-bg-muted">
          <EmptyState
            icon={<AlertTriangle />}
            title={t("No se pudieron cargar los tickets")}
            description={t("Hubo un problema de conexión. Inténtalo de nuevo.")}
            action={
              <Button variant="outline" onClick={() => load()}>
                {t("Reintentar")}
              </Button>
            }
          />
        </div>
      ) : !loading && visible.length === 0 ? (
        <div className="rounded-lg border border-border bg-bg-muted">
          <EmptyState
            icon={<TicketIcon />}
            title={t("No hay tickets")}
            description={
              activeFilters || debouncedSearch || statFilter
                ? t("Prueba con otros filtros o limpia la búsqueda.")
                : t("No se reportaron incidencias en este rango.")
            }
            action={
              (activeFilters || debouncedSearch || statFilter) && (
                <Button variant="outline" onClick={clearFilters}>
                  {t("Limpiar filtros")}
                </Button>
              )
            }
          />
        </div>
      ) : view === "board" ? (
        <TicketBoard
          incidents={visible}
          loading={loading}
          localColumns={localColumns}
          isMobile={isMobile}
          onOpen={(inc) => setSelectedId(inc._id)}
          onMoveColumn={handleMoveColumn}
        />
      ) : (
        <TicketListTable
          incidents={visible}
          loading={loading}
          localColumns={localColumns}
          onOpen={(inc) => setSelectedId(inc._id)}
        />
      )}

      {/* ── Detalle ── */}
      {selectedId && (
        <TicketDetailDrawer
          incidentId={selectedId}
          onClose={() => {
            setSelectedId(null);
            load();
          }}
          onUpdated={() => load()}
        />
      )}
    </div>
  );
}

function columnLabel(t, key) {
  return (
    {
      nuevo: t("Nuevo"),
      en_proceso: t("En proceso"),
      esperando: t("Esperando"),
      resuelto: t("Resuelto"),
      cerrado: t("Cerrado"),
    }[key] || key
  );
}

function ViewToggle({ active, onClick, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 h-8 text-sm font-medium rounded-md transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "bg-brand-500 text-white" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
      )}
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}

function TicketListTable({ incidents, loading, localColumns, onOpen }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (loading) {
    if (isMobile) {
      return (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`tl-sk-${i}`} className="rounded-lg border border-border bg-bg p-4 shadow-card space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      );
    }
    // 8 columnas para espejar la tabla real (Pedido, Tipificación, Empresa,
    // Responsable, Área, Criticidad, Estado, Creada).
    return <TableSkeleton rows={8} columns={8} />;
  }

  // ── Mobile: cards en vez de tabla densa ──
  if (isMobile) {
    return (
      <div className="space-y-3">
        {incidents.map((inc) => {
          const person = assignee(inc);
          const col = columnFor(inc, localColumns[inc._id]);
          const dept = primaryDepartment(inc);
          return (
            <button
              key={inc._id}
              type="button"
              onClick={() => onOpen(inc)}
              className="w-full text-left rounded-lg border border-border bg-bg p-4 shadow-card space-y-3 transition-colors hover:border-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 font-mono text-xs text-fg">
                  <Package className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
                  {followingNumber(inc) || "—"}
                </span>
                <Badge tone={COLUMN_TONE[col] || "neutral"} dot>
                  {columnLabel(t, col)}
                </Badge>
              </div>
              <div>
                <div className="font-medium text-fg truncate">{typeLabel(inc) || t("Sin tipificar")}</div>
                {inc.description && (
                  <div className="text-xs text-fg-muted truncate">{inc.description}</div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">{t(dept.label)}</Badge>
                {inc.severity && (
                  <Badge tone={SEVERITY_TONE[inc.severity] || "neutral"} dot>
                    {t(INCIDENT_SEVERITY[inc.severity] || inc.severity)}
                  </Badge>
                )}
                <span className="text-xs text-fg-muted ml-auto whitespace-nowrap">
                  {moment(inc.createdAt).format("DD/MM/YYYY HH:mm")}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-border pt-2.5">
                <span className="text-sm text-fg truncate">{clientName(inc) || "—"}</span>
                {person ? (
                  <span className="inline-flex items-center gap-2 shrink-0">
                    <Avatar size="xs" initials={getInitials(person.name, person.lastName)} />
                    <span className="text-sm text-fg truncate max-w-[120px]">{person.name}</span>
                  </span>
                ) : (
                  <span className="text-sm text-fg-subtle italic shrink-0">{t("Sin asignar")}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-bg overflow-hidden shadow-card">
      <Table>
        <THead>
          <tr>
            <TH>{t("Pedido")}</TH>
            <TH>{t("Tipificación")}</TH>
            <TH>{t("Empresa")}</TH>
            <TH>{t("Responsable")}</TH>
            <TH>{t("Área")}</TH>
            <TH>{t("Criticidad")}</TH>
            <TH>{t("Estado")}</TH>
            <TH>{t("Creada")}</TH>
          </tr>
        </THead>
        <TBody>
          {incidents.map((inc) => {
            const person = assignee(inc);
            const col = columnFor(inc, localColumns[inc._id]);
            const dept = primaryDepartment(inc);
            return (
              <TR
                key={inc._id}
                className="cursor-pointer"
                onClick={() => onOpen(inc)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onOpen(inc);
                }}
              >
                <TD>
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs text-fg">
                    <Package className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
                    {followingNumber(inc) || "—"}
                  </span>
                </TD>
                <TD>
                  <div className="max-w-[260px] truncate font-medium text-fg">
                    {typeLabel(inc) || t("Sin tipificar")}
                  </div>
                  {inc.description && (
                    <div className="max-w-[260px] truncate text-xs text-fg-muted">
                      {inc.description}
                    </div>
                  )}
                </TD>
                <TD>
                  <span className="text-sm text-fg">{clientName(inc) || "—"}</span>
                </TD>
                <TD>
                  {person ? (
                    <span className="inline-flex items-center gap-2">
                      <Avatar size="xs" initials={getInitials(person.name, person.lastName)} />
                      <span className="text-sm text-fg truncate max-w-[120px]">{person.name}</span>
                    </span>
                  ) : (
                    <span className="text-sm text-fg-subtle italic">{t("Sin asignar")}</span>
                  )}
                </TD>
                <TD>
                  <Badge tone="neutral">{t(dept.label)}</Badge>
                </TD>
                <TD>
                  {inc.severity ? (
                    <Badge tone={SEVERITY_TONE[inc.severity] || "neutral"} dot>
                      {t(INCIDENT_SEVERITY[inc.severity] || inc.severity)}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </TD>
                <TD>
                  <Badge tone={COLUMN_TONE[col] || "neutral"} dot>
                    {columnLabel(t, col)}
                  </Badge>
                </TD>
                <TD>
                  <span className="text-xs text-fg-muted whitespace-nowrap">
                    {moment(inc.createdAt).format("DD/MM/YYYY HH:mm")}
                  </span>
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}
